import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { ROUTES, USER_ROLES } from '../constants';
import { selectCurrentUserIsAdmin, selectCurrentUserIsPortalAdmin, selectCurrentUserRole, selectIdToken, selectUserOrgId } from '../../auth/redux/slice';
import { selectConfig } from '../redux/slice';
import { SimplyTestApi } from '../../api/simplytest';
import { OrganizationItem } from '../../api/types';
import { IConfiguration } from '../types';

export type RequireOrderIntentProps = {
    // Sep 2026 - '/orders/new' ("Create a standard order") is Portal-Admin-only now, not every
    // true (multi-org) admin - CreateOrderIntentModal only offers it to a Portal Admin.
    // requireTrueAdmin enforces that same, narrower rule at the route level (the prop name is a
    // bit stale post-restriction, kept as-is rather than renaming every call site for a wording
    // change alone).
    requireTrueAdmin?: boolean;
    children: React.ReactNode;
};

// Guards the Pre-Buy "new order" routes (/orders/new, /kits/new, /orders/new/patient,
// /orders/new/clinic) against direct URL access. CreateOrderIntentModal already decides which of
// these options a user is offered, but that's UI-only - typing the URL bypassed it entirely. This
// re-checks the exact same rule the modal uses (Portal-Admin-only for /orders/new specifically,
// isTrueAdmin/preBuyEnabled for the Pre-Buy kit-flow routes), so the route itself enforces it, not
// just the click handler that normally leads here.
//
// Fetches the organization directly via SimplyTestApi rather than through the shared
// organizations/add-edit redux slice - that slice's loadOrganization saga never dispatches
// setState('READY') on completion, so selectOrganizationAddEditIsBusy stays stuck on its initial
// 'BUSY' value forever, which would leave this guard showing its spinner indefinitely.
export const RequireOrderIntent: React.FC<RequireOrderIntentProps> = ({ requireTrueAdmin, children }) => {
    const config: IConfiguration = useSelector(selectConfig)!;
    const idToken = useSelector(selectIdToken);
    const client = useMemo(() => new SimplyTestApi(config.apiBaseUrl, idToken), [config.apiBaseUrl, idToken]);

    const isAdmin = useSelector(selectCurrentUserIsAdmin);
    const isPortalAdmin = useSelector(selectCurrentUserIsPortalAdmin);
    const roleName = useSelector(selectCurrentUserRole);
    const userOrgId = useSelector(selectUserOrgId);
    // Same distinction CreateOrderIntentModal/CreateOrderAction make - Organization Admin(+Provider)
    // belong to a single, known org, so they're gated on that org's own flag like anyone else. Only
    // a true multi-org admin is exempt from the Pre-Buy kit-flow routes' own check below.
    const isTrueAdmin = isAdmin && roleName !== USER_ROLES.OrganizationAdminProvider && roleName !== USER_ROLES.OrganizationAdmin;
    // Sep 2026 - the bypass for requireTrueAdmin (the /orders/new standard-order route) is
    // Portal-Admin-only now, deliberately narrower than isTrueAdmin above - a User Group Admin or
    // generic Admin still gets the isTrueAdmin bypass on every OTHER RequireOrderIntent route
    // (the Pre-Buy kit flows), just not this one.
    const bypassesOrgCheck = requireTrueAdmin ? isPortalAdmin : isTrueAdmin;

    const [organization, setOrganization] = useState<OrganizationItem | undefined>();
    const [loadState, setLoadState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');

    useEffect(() => {
        if (!userOrgId || bypassesOrgCheck) return;
        let cancelled = false;
        setLoadState('loading');
        client.getOrganization(userOrgId)
            .then(res => { if (!cancelled) { setOrganization(res.data); setLoadState('loaded'); } })
            .catch(() => { if (!cancelled) setLoadState('error'); });
        return () => { cancelled = true; };
    }, [client, userOrgId, bypassesOrgCheck]);

    if (bypassesOrgCheck) return <>{children}</>;

    if (userOrgId && (loadState === 'idle' || loadState === 'loading')) {
        return (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
                <CircularProgress size={36} thickness={4} sx={{ color: '#0891B2' }} />
            </Box>
        );
    }

    // requireTrueAdmin (/orders/new, the standard order form) is for Portal Admins (bypassed
    // above) OR org-scoped users whose own org has no Pre-Buy access at all - CreateOrderAction.tsx
    // links straight here for exactly that case, since there's no kit flow available to them
    // either way. A Pre-Buy-enabled org's admin must go through the intent modal's kit flows
    // instead, so this route rejects them. The non-requireTrueAdmin routes (the Pre-Buy kit flows
    // themselves) are the mirror image - only an org with Pre-Buy actually enabled may use them.
    const preBuyEnabled = !!organization?.preBuyEnabled;
    // Sep 2026 - userOrgId ? ... : false (not a bare default-true) - a user with no org at all AND
    // not Portal-Admin-bypassed above (e.g. a User Group Admin/generic Admin with no single-org
    // context) has no Pre-Buy status to check either way, and must fail closed here now that this
    // route is deliberately narrower than "every true admin" - previously this fell through to an
    // implicit "allowed" for exactly that case, which was fine when they'd have bypassed above
    // anyway, but would now be a real gap in the Portal-Admin-only restriction.
    const allowed = requireTrueAdmin ? (userOrgId ? !preBuyEnabled : false) : preBuyEnabled;

    // Fail closed on a load error or a disallowed combination, rather than letting an
    // indeterminate state fall through.
    if (loadState === 'error' || !allowed) return <Navigate to={ROUTES.Orders} replace />;

    return <>{children}</>;
};
